import type { AutomationResultRecord } from "@/lib/api/generated/control-api.schemas";

type JsonRecord = Record<string, unknown>;

export type AutomationDigestItem = {
  title: string;
  source: string | null;
  url: string | null;
  takeaway: string | null;
};

export type AutomationResultViewModel = {
  title: string | null;
  content: string | null;
  summary: string;
  digestItems: AutomationDigestItem[];
  payload: JsonRecord | null;
  workflowResult: JsonRecord | null;
};

function asRecord(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as JsonRecord)
    : null;
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function readNestedRecord(
  record: JsonRecord | null,
  keys: string[],
): JsonRecord | null {
  let current = record;
  for (const key of keys) {
    current = asRecord(current?.[key]);
    if (!current) {
      return null;
    }
  }

  return current;
}

function getPayload(result: AutomationResultRecord): JsonRecord | null {
  const directPayload = asRecord(result.payload);
  if (directPayload) {
    return directPayload;
  }

  return readNestedRecord(asRecord(result.workflowResult), [
    "result",
    "payload",
  ]);
}

function getTitle(
  result: AutomationResultRecord,
  payload: JsonRecord | null,
  workflowResult: JsonRecord | null,
): string | null {
  return (
    asString(result.title) ??
    asString(payload?.title) ??
    asString(readNestedRecord(workflowResult, ["result"])?.title) ??
    null
  );
}

function getContent(
  result: AutomationResultRecord,
  payload: JsonRecord | null,
  workflowResult: JsonRecord | null,
): string | null {
  return (
    asString(result.content) ??
    asString(payload?.content) ??
    asString(payload?.markdown) ??
    asString(payload?.markdownText) ??
    asString(payload?.contentMarkdown) ??
    asString(readNestedRecord(workflowResult, ["result"])?.content) ??
    asString(readNestedRecord(workflowResult, ["result"])?.markdown) ??
    asString(readNestedRecord(workflowResult, ["result"])?.markdownText) ??
    null
  );
}

function getDigestItems(payload: JsonRecord | null): AutomationDigestItem[] {
  const digest = payload?.digest;
  if (!Array.isArray(digest)) {
    return [];
  }

  return digest.flatMap((item): AutomationDigestItem[] => {
    const record = asRecord(item);
    if (!record) {
      return [];
    }

    const title = asString(record.title) ?? asString(record.headline);
    if (!title) {
      return [];
    }

    return [
      {
        title,
        source: asString(record.source),
        url: asString(record.url),
        takeaway:
          asString(record.takeaway) ??
          asString(record.summary) ??
          asString(record.note),
      },
    ];
  });
}

export function createAutomationResultViewModel(
  result: AutomationResultRecord,
): AutomationResultViewModel {
  const payload = getPayload(result);
  const workflowResult = asRecord(result.workflowResult);

  return {
    title: getTitle(result, payload, workflowResult),
    content: getContent(result, payload, workflowResult),
    summary: result.summary.trim(),
    digestItems: getDigestItems(payload),
    payload,
    workflowResult,
  };
}
